import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Crown, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { errText, useAuth } from "@/lib/auth";
import axios from "axios";
import { API } from "@/lib/auth";
import { ModeCard, useMode } from "@/components/engage/ModeCard";
import { ReferralCard } from "@/components/engage/Referral";
import { NotifyCard } from "@/components/engage/NotifyCard";

export function Account({ initialMode = "login" }) {
  const { profile, signIn, signOut } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [f, setF] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  if (profile === undefined) return null;
  if (profile) return (
    <div className="space-y-4 max-w-2xl" data-testid="account-page">
      <h1 className="font-head font-bold text-3xl tracking-tight">Account</h1>
      <section className="glass p-5 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-slate-400">Name</span><span data-testid="account-name">{profile.name}</span></div>
        <div className="flex justify-between"><span className="text-slate-400">Email</span><span data-testid="account-email">{profile.email}</span></div>
        <div className="flex justify-between"><span className="text-slate-400">Plan</span>
          <span data-testid="account-plan">{profile.plan !== "free" ? <span className="pill-pro">{profile.plan.toUpperCase()}</span> : "Free"}</span></div>
        <div className="flex justify-between"><span className="text-slate-400">Limits</span><span className="text-right">{profile.limits.watchlist_max} watchlist · {profile.limits.alerts_max} alerts</span></div>
      </section>
      {profile.plan !== "elite" && <button onClick={() => nav("/pro")} className="btn-primary w-full" data-testid="account-upgrade-btn">{profile.plan === "pro" ? "Upgrade to Elite" : "See membership plans"}</button>}
      <ModeCard />
      <NotifyCard />
      <ReferralCard />
      <button onClick={signOut} className="btn-ghost w-full" data-testid="account-signout-btn">Sign out</button>
    </div>
  );
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await signIn(mode, mode === "register" ? f : { email: f.email, password: f.password });
      toast.success(mode === "register" ? "Account created" : "Welcome back");
      nav("/");
    } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-sm mx-auto pt-6" data-testid="auth-page">
      <h1 className="font-head font-bold text-3xl tracking-tight text-center">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
      <p className="text-sm text-slate-400 text-center mt-1">Free forever plan. No card needed.</p>
      <form onSubmit={submit} className="glass p-5 mt-6 space-y-3">
        {mode === "register" && <Input placeholder="Name (optional)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-11 bg-[var(--bg)] border-[var(--line)]" data-testid="auth-name-input" />}
        <Input type="email" required placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-11 bg-[var(--bg)] border-[var(--line)]" data-testid="auth-email-input" />
        <Input type="password" required minLength={mode === "register" ? 8 : 1} placeholder={mode === "register" ? "Password (8+ characters)" : "Password"} value={f.password}
          onChange={(e) => setF({ ...f, password: e.target.value })} className="h-11 bg-[var(--bg)] border-[var(--line)]" data-testid="auth-password-input" />
        <button disabled={busy} className="btn-primary w-full" data-testid="auth-submit-btn">{busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}</button>
      </form>
      <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="w-full text-sm text-[var(--cyan)] mt-4" data-testid="auth-toggle-mode">
        {mode === "login" ? "New here? Create an account" : "Have an account? Sign in"}
      </button>
    </div>
  );
}

const TIERS = [
  { id: "free", name: "Free", m: 0, y: 0, icon: null, feats: ["Live BTC, ETH & SOL prices and charts", "All prediction markets: sports, politics, culture & more", "3 data picks per category", "Practice betting with $1,000 paper money", "Beginner handbooks", "5 AI questions a day", "5 watchlist coins · 2 price alerts"] },
  { id: "pro", name: "Pro", m: 4.99, y: 47.88, feats: ["Everything in Free", "15 data picks per category + 1-hour price movement", "Intermediate handbooks (all 4 topics)", "Full BTC 15-min analytics & track record", "Insights for any top-250 coin", "50 AI questions a day", "50 watchlist coins · 25 price alerts"] },
  { id: "elite", name: "Elite", m: 14.99, y: 143.88, feats: ["Everything in Pro", "Every data pick in every category", "Advanced handbooks with live drills", "All future handbooks included", "Unlimited AI assistant", "200 watchlist coins · 100 price alerts"] },
];

const PlanCard = ({ t, yearly, current, busy, onPick, trial }) => {
  const price = yearly ? t.y / 12 : t.m;
  const hot = t.id === "elite";
  return (
    <section className={`glass p-6 relative overflow-hidden flex flex-col ${hot ? "ring-1 ring-[var(--violet)]/60" : t.id === "pro" ? "ring-1 ring-[var(--cyan)]/40" : ""}`} data-testid={`plan-${t.id}`}>
      {hot && <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[var(--violet)]/20 blur-3xl" />}
      <div className={`text-sm flex items-center gap-1 ${hot ? "text-[var(--violet)]" : t.id === "pro" ? "text-[var(--cyan)]" : "text-slate-400"}`}>{t.id !== "free" && <Crown className="w-4 h-4" />}{t.name}</div>
      <div className="font-head text-3xl font-bold mt-1 font-num" data-testid={`plan-${t.id}-price`}>${price.toFixed(2)}<span className="text-base text-slate-400 font-normal">/month</span></div>
      <div className="text-xs text-slate-500 h-4">{yearly && t.y ? `$${t.y.toFixed(2)} billed yearly · save ${Math.round((1 - t.y / (t.m * 12)) * 100)}%` : t.m ? "billed monthly" : ""}</div>
      <ul className="mt-4 space-y-2 text-sm flex-1">{t.feats.map((x) => <li key={x} className="flex gap-2"><Check className={`w-4 h-4 shrink-0 mt-0.5 ${t.id === "free" ? "text-slate-500" : "text-[var(--cyan)]"}`} />{x}</li>)}</ul>
      {current === t.id ? <div className="mt-6 text-center text-emerald-300 font-semibold" data-testid={`plan-${t.id}-current`}>Your plan</div> :
        t.id !== "free" && <button onClick={() => onPick(`${t.id}_${yearly ? "yearly" : "monthly"}`)} disabled={!!busy} className={`${hot ? "btn-primary" : "btn-ghost"} w-full mt-6`} data-testid={`plan-${t.id}-checkout-btn`}>
          {busy === t.id ? "Opening secure checkout…" : trial && t.id === "pro" ? "Start 7-day free trial" : `Get ${t.name}`}</button>}
      {trial && t.id === "pro" && current !== "pro" && <div className="text-[11px] text-emerald-300 text-center mt-2" data-testid="plan-pro-trial-note">7 days free, then billed. Cancel any time.</div>}
    </section>
  );
};

export function Pro() {
  const { profile } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);
  const [yearly, setYearly] = useState(false);
  const [md] = useMode();
  const trial = !profile || md?.trial_eligible;
  const go = async (plan) => {
    if (!profile) return nav("/account");
    setBusy(plan.split("_")[0]);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, { origin_url: window.location.origin, plan });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errText(e)); setBusy(null); }
  };
  return (
    <div className="space-y-6" data-testid="pro-page">
      <div className="text-center pt-4">
        <span className="pill-pro">MEMBERSHIP</span>
        <h1 className="font-head font-bold text-4xl tracking-tight mt-3">Pick the plan that fits how you trade.</h1>
        <p className="text-sm text-slate-400 mt-2">Educational market analytics. No guaranteed results — ever. Cancel any time.</p>
      </div>
      <div className="flex justify-center">
        <div className="inline-flex rounded-full border border-[var(--line)] p-1" data-testid="billing-toggle">
          {[[false, "Monthly"], [true, "Yearly · save 20%"]].map(([v, l]) => (
            <button key={l} onClick={() => setYearly(v)} data-testid={`billing-${v ? "yearly" : "monthly"}`}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${yearly === v ? "bg-[var(--cyan)] text-[#03141A]" : "text-slate-400"}`}>{l}</button>))}
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
        {TIERS.map((t) => <PlanCard key={t.id} t={t} trial={trial} yearly={yearly} current={profile ? profile.plan : null} busy={busy} onPick={go} />)}
      </div>
      {!profile && <p className="text-center text-sm text-slate-400">You'll be asked to sign in first.</p>}
      <p className="text-xs text-slate-500 text-center flex items-center justify-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />Payments by Stripe (currently in test mode). Switching plans cancels your old one automatically.</p>
    </div>
  );
}
